(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const isUnsafeBadgeLabel = (label) => {
    const value = String(label || '').trim();
    const lower = value.toLowerCase();
    return (
      !value ||
      lower.includes('sa_') ||
      lower.includes('class=') ||
      lower.includes('style=') ||
      lower.includes('product_description_dot') ||
      lower.includes('<') ||
      lower.includes('>') ||
      lower.includes('&lt;') ||
      lower.includes('&gt;') ||
      lower.includes('script') ||
      lower.includes('onclick') ||
      lower.includes('onerror') ||
      lower.includes('onload')
    );
  };

  const safeBadgeContainer = (container) => {
    const labels = Array.from(container.children)
      .filter((child) => child.tagName === 'SPAN')
      .map((child) => child.textContent.trim())
      .filter((label, index, list) => !isUnsafeBadgeLabel(label) && list.indexOf(label) === index);

    if (!labels.length) return null;

    const safeContainer = document.createElement('div');
    safeContainer.className = 'product-conversion__glance-chips';
    labels.slice(0, 10).forEach((label) => {
      const span = document.createElement('span');
      span.textContent = label;
      safeContainer.appendChild(span);
    });
    return safeContainer;
  };

  const isBadgeContainer = (element) => (
    element &&
    element.tagName === 'DIV' &&
    Array.from(element.children).filter((child) => child.tagName === 'SPAN').length > 1
  );

  const findSummaryBadgeContainer = (root) => {
    const summaryContent = root.querySelector('[data-conversion-slot="quick-buying-summary"] .product-conversion__summary-content')
      || root.querySelector('[data-conversion-slot="quick-buying-summary"]');

    if (!summaryContent) return null;

    return [summaryContent, ...Array.from(summaryContent.querySelectorAll('div'))].find(isBadgeContainer);
  };

  const hydrateGlanceCard = (root) => {
    const glanceBadges = root.querySelector('[data-conversion-glance-badges]');
    if (!glanceBadges) return;

    const badgeContainer = findSummaryBadgeContainer(root);
    if (!badgeContainer) return;

    if (glanceBadges.querySelector('span')) {
      badgeContainer.remove();
    } else {
      const safeContainer = safeBadgeContainer(badgeContainer);
      badgeContainer.remove();
      if (safeContainer) glanceBadges.replaceChildren(safeContainer);
    }
  };

  const setActiveLink = (links, activeId) => {
    links.forEach((link) => {
      const isActive = link.hash === `#${activeId}`;
      if (isActive) {
        link.setAttribute('aria-current', 'location');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  const initProductSections = (root) => {
    const links = Array.from(root.querySelectorAll('.sa-product-sections__nav a[href^="#"]'));
    const sectionEntries = links
      .map((link) => {
        const target = document.getElementById(link.hash.slice(1));
        if (!target) return null;
        const scrollTarget = target.classList.contains('sa-product-sections__anchor')
          ? target.closest('h2, h3, h4') || target
          : target;

        return { link, target, scrollTarget };
      })
      .filter(Boolean);
    const sections = sectionEntries.map((entry) => entry.target);

    if (!links.length || !sections.length) return;
    if (root.dataset.saProductSectionsBound === 'true') return;
    root.dataset.saProductSectionsBound = 'true';

    const getTopOffset = () => (window.matchMedia('(max-width: 991px)').matches ? 92 : 132);

    const getScrollTop = (entry) => entry.scrollTarget.getBoundingClientRect().top + window.scrollY;

    let scrollSpyFrame = 0;
    const updateActiveFromScroll = () => {
      scrollSpyFrame = 0;
      const activationPoint = window.scrollY + getTopOffset() + 4;
      const activeEntry = sectionEntries.reduce((active, entry) => (
        getScrollTop(entry) <= activationPoint ? entry : active
      ), sectionEntries[0]);

      if (activeEntry) setActiveLink(links, activeEntry.target.id);
    };

    const queueActiveFromScroll = () => {
      if (scrollSpyFrame) return;
      scrollSpyFrame = window.requestAnimationFrame(updateActiveFromScroll);
    };

    links.forEach((link) => {
      link.addEventListener('click', (event) => {
        const entry = sectionEntries.find((sectionEntry) => sectionEntry.link === link);
        if (!entry) return;
        const top = getScrollTop(entry) - getTopOffset();

        event.preventDefault();
        setActiveLink(links, entry.target.id);
        window.scrollTo({
          top: Math.max(0, top),
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
        entry.target.focus({ preventScroll: true });
        history.pushState(null, '', link.hash);
        window.setTimeout(updateActiveFromScroll, reducedMotion.matches ? 80 : 700);
      });
    });

    updateActiveFromScroll();
    window.addEventListener('scroll', queueActiveFromScroll, { passive: true });
    window.addEventListener('resize', queueActiveFromScroll);
  };

  const initPaymentOptions = (root) => {
    const panel = root.querySelector('[data-sa-payment-options]');
    if (!panel) return;

    const sectionRoot = root.closest('[id^="MainProduct-"]');
    const sectionId = sectionRoot?.id || 'product';
    panel.id = panel.id || `sa-product-payment-options-${sectionId}`;

    const syncMoreOptions = () => {
      const moreOptions = root.querySelector('.shopify-payment-button__more-options');
      if (!moreOptions) return false;

      moreOptions.setAttribute('aria-expanded', panel.hidden ? 'false' : 'true');
      moreOptions.setAttribute('aria-controls', panel.id);

      return true;
    };

    const openPaymentPanel = (moreOptions) => {
      panel.hidden = false;
      moreOptions.setAttribute('aria-expanded', 'true');
      moreOptions.setAttribute('aria-controls', panel.id);

      const firstOption = panel.querySelector('[data-sa-payment-option]');
      if (firstOption) firstOption.focus({ preventScroll: true });
    };

    if (root.dataset.saPaymentOptionsDelegateBound !== 'true') {
      root.dataset.saPaymentOptionsDelegateBound = 'true';

      root.addEventListener('click', (event) => {
        const selectedOption = event.target.closest('[data-sa-payment-option]');

        if (selectedOption && panel.contains(selectedOption)) {
          const moreOptions = root.querySelector('.shopify-payment-button__more-options');
          if (!moreOptions) return;

          moreOptions.dataset.saAllowCheckout = 'true';
          moreOptions.click();
          window.setTimeout(() => {
            delete moreOptions.dataset.saAllowCheckout;
          }, 0);
          return;
        }

        const moreOptions = event.target.closest('.shopify-payment-button__more-options');
        if (!moreOptions || !root.contains(moreOptions)) return;
        if (moreOptions.dataset.saAllowCheckout === 'true') return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        openPaymentPanel(moreOptions);
      }, true);
    }

    syncMoreOptions();
    if (!('MutationObserver' in window)) return;

    const observer = new MutationObserver(() => {
      syncMoreOptions();
    });

    observer.observe(root, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 30000);
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.product-page-section--conversion').forEach(hydrateGlanceCard);
    document.querySelectorAll('.product-page-section--conversion').forEach(initPaymentOptions);
    document.querySelectorAll('[data-sa-product-sections]').forEach(initProductSections);
  });

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('.product-page-section--conversion').forEach(hydrateGlanceCard);
    event.target.querySelectorAll('.product-page-section--conversion').forEach(initPaymentOptions);
    event.target.querySelectorAll('[data-sa-product-sections]').forEach(initProductSections);
  });
})();
